import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import {
  COHORT_METHOD_OPTIONS,
  bedFileIdOf,
  buildCarrierRequest,
  buildCohortRequest,
  buildMultisampleRequest,
  describeWorkflow,
  newBatch,
  startRequests,
  suggestBatchName,
  validateCarrierGroup,
  validateCohort,
} from 'src/services/analysisStart'
import { BATCHES_QUERY_KEY, batchPath } from 'src/views/Batches/useBatches'
import { ANALYSIS_STATS_KEY } from 'src/views/AnalysisView/useAnalysisStats'
import { validateWorkflow } from '../model/workflow'
import { balanceOf, creditsText, formatCredits, groupPrice } from '../model/pricing'
import { isEnabled } from '../model/features'
import MultisampleArea from './MultisampleArea'
import CohortArea from './CohortArea'
import CarrierArea from './CarrierArea'
import { GroupReviewView, GroupStartView } from './GroupReview'
import {
  carrierSlotLabels,
  filledGroups,
  groupIssues,
  groupLabel,
  normaliseGroupState,
  resizeCarrierGroups,
  setCohortKit,
  toggleCohortMember,
} from './model'

// ----------------------------------------------------------------------
// The group analyses of the New analysis panel: Multisample, CNV cohort and Carrier
// (behind FEATURES.carrier). The panel calls this hook on every render with
// `active` = isGroupAnalysis(workflow) and, while active, shows what it returns in
// place of its own samples area, review, footer and header:
//
//   const flow = useGroupFlow({ active, workflow, bedFiles, candidates, fileMap, org,
//     initialState, onStateChange, onStarted, onClose, showErrors })
//   flow = { view, samplesArea, body, footer: { primary, secondary, text, emphasis },
//            headerTitle, headerCount, running, stop, showErrors }
//
// The group state (see ./model) is read from `initialState` once, on mount; every
// change goes to onStateChange so the panel's draft keeps it. Starts go through
// startRequests only, one request per multisample or carrier group and one for a
// cohort, in order; a 402 halts the rest. After a run the groups (or the cohort
// selection) that started, or may have started, are pruned, and failed ones kept.
//
// Optional extras the panel may pass: batchName (the carrier batch name; defaults to
// suggestBatchName) and onShowErrors (called when a blocked Review is clicked, so
// the column can show its errors). flow.showErrors is true once that happened.
// ----------------------------------------------------------------------

// Review and Start share one button slot: clicks on Start this soon after Review
// opened are ignored, so a double-click on Review never starts a run.
export const START_GUARD_MS = 700

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

const INERT_FOOTER = Object.freeze({ primary: null, secondary: null, text: '', emphasis: false })

const METHOD_LABEL = Object.fromEntries(COHORT_METHOD_OPTIONS.map((o) => [o.value, o.label]))

// The footer's words for the first thing that keeps a multisample group from starting.
const multisampleBlockText = (issue, label) => {
  switch (issue.code) {
    case 'too_few':
      return `Add at least 2 samples to ${label}`
    case 'no_relation':
      return `Add a relation for each sample in ${label}`
    case 'none_included':
      return `Include at least one sample in ${label}`
    case 'not_finished':
      return `${label} has an analysis that is not finished yet`
    case 'duplicate':
      return `${label} has the same analysis twice`
    default:
      return `${label}: ${issue.text}`
  }
}

const COHORT_BLOCK_TEXT = {
  bad_method: 'Choose a cohort method',
  no_kit: 'Choose a capture kit',
  kit_missing: 'The capture kit is not in your kit list. Choose it again.',
  no_name: 'Name the cohort',
  too_few: 'Select at least 2 analyses',
  too_many: 'Select 50 analyses at most',
  duplicate_sample: 'Two analyses of one sample. Keep one.',
}

const stripStop = (text) => (text || '').replace(/\.$/, '')

const kindOf = (active, analysis) => {
  if (!active) return null
  if (analysis === 'multisample' || analysis === 'cohort') return analysis
  if (analysis === 'carrier' && isEnabled('carrier')) return 'carrier'
  return null
}

/**
 * The group state without what a run started, or may have started (`gone`: item
 * keys). Retrying those could start and charge them twice. A cohort is one item, so
 * its selection empties.
 */
export const pruneStarted = (state, runKind, gone) => {
  if (!gone || !gone.size) return state
  if (runKind === 'multisample') {
    return { ...state, multisample: { groups: state.multisample.groups.filter((g) => !gone.has(g.uid)) } }
  }
  if (runKind === 'carrier') {
    return { ...state, carrier: { groups: state.carrier.groups.filter((g) => !gone.has(g.uid)) } }
  }
  return { ...state, cohort: { kitId: state.cohort.kitId, members: [] } }
}

const useGroupFlow = ({
  active,
  workflow,
  bedFiles,
  candidates,
  org,
  initialState,
  onStateChange,
  onStarted,
  onClose,
  showErrors,
  batchName,
  onShowErrors,
}) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const wf = workflow || {}
  const kind = kindOf(active, wf.analysis)

  const [state, setState] = useState(() => normaliseGroupState(initialState))
  // The latest state, for a run that ends after the panel has closed.
  const stateRef = useRef(state)
  stateRef.current = state
  const [view, setView] = useState('compose')
  const [run, setRun] = useState(null)
  const [attempted, setAttempted] = useState(false)
  const reviewEnteredAtRef = useRef(0)
  const runStartedAtRef = useRef(0)
  const abortRef = useRef(null)
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  // Every change goes to the panel (its draft); the state read on mount does not.
  const onStateChangeRef = useRef(onStateChange)
  useEffect(() => {
    onStateChangeRef.current = onStateChange
  })
  const firstStateRef = useRef(true)
  useEffect(() => {
    if (firstStateRef.current) {
      firstStateRef.current = false
      return
    }
    if (onStateChangeRef.current) onStateChangeRef.current(state)
  }, [state])

  // Another analysis type starts from its compose view.
  useEffect(() => {
    if (abortRef.current) return
    setView('compose')
    setRun(null)
    setAttempted(false)
  }, [kind])

  // A CNV cohort belongs to one kit: choosing another clears the selection.
  const kitId = wf.kitId || ''
  useEffect(() => {
    if (kind !== 'cohort' || !kitId) return
    setState((prev) => (prev.cohort.kitId === kitId ? prev : { ...prev, cohort: setCohortKit(prev.cohort, kitId) }))
  }, [kind, kitId])

  // Carrier groups follow the panel's sample count.
  const samplesRequired = Number(wf.carrierSamplesRequired) || 0
  useEffect(() => {
    if (kind !== 'carrier' || !samplesRequired) return
    setState((prev) => {
      if (prev.carrier.groups.every((g) => g.members.length === samplesRequired)) return prev
      return { ...prev, carrier: { groups: resizeCarrierGroups(prev.carrier.groups, samplesRequired) } }
    })
  }, [kind, samplesRequired])

  // ------------------------------------------------------------ derived
  const columnErrors = kind ? validateWorkflow(wf) : {}
  const firstColumnError = ['kit', 'cohortMethod', 'cohortName', 'carrierConfig']
    .map((key) => columnErrors[key])
    .find(Boolean)

  const msGroups = filledGroups(state.multisample.groups)
  const msSampleCount = msGroups.reduce((n, g) => n + g.members.length, 0)

  const cohortMembers = state.cohort.kitId === kitId ? state.cohort.members : []
  const bedFileId = bedFileIdOf(bedFiles, kitId)
  const cohortMethod = wf.cohortMethod || 'genesus-cnv'
  const cohortName = (wf.cohortName || '').trim()

  const carrierConfig = wf.carrierConfigId
    ? { id: wf.carrierConfigId, title: wf.carrierConfigTitle || '', samplesRequired: samplesRequired || 2 }
    : null
  const carrierNoun = carrierSlotLabels(carrierConfig?.samplesRequired).length === 2 ? 'couple' : 'group'
  const carrierGroups = state.carrier.groups.filter((g) => g.members.some((m) => m && m.fileId))
  const allowFastq = isEnabled('carrierFastq')

  // The first thing that keeps the run from starting, in the footer's words, or ''.
  const firstBlock = () => {
    if (!kind) return ''
    if (firstColumnError) return stripStop(firstColumnError)
    if (kind === 'multisample') {
      if (!msGroups.length) return 'Pick finished analyses to continue'
      const blocked = msGroups.map((group) => ({ group, issue: groupIssues(group)[0] })).find((g) => g.issue)
      if (!blocked) return ''
      return multisampleBlockText(
        blocked.issue,
        groupLabel(blocked.group, state.multisample.groups.indexOf(blocked.group)),
      )
    }
    if (kind === 'cohort') {
      const issue = validateCohort({
        method: cohortMethod,
        name: cohortName,
        kitId,
        bedFileId,
        members: cohortMembers,
      })[0]
      return issue ? COHORT_BLOCK_TEXT[issue.code] || stripStop(issue.message) : ''
    }
    if (!carrierGroups.length) return `Add a ${carrierNoun} to continue`
    const config = { id: carrierConfig.id, samples_required: carrierConfig.samplesRequired }
    const blocked = carrierGroups
      .map((group) => ({ group, issue: validateCarrierGroup({ config, members: group.members, allowFastq })[0] }))
      .find((g) => g.issue)
    if (!blocked) return ''
    const what =
      blocked.issue.code === 'fastq_unavailable'
        ? 'uses a FASTQ file. Carrier runs on VCFs for now'
        : blocked.issue.code === 'duplicate'
        ? 'has the same file twice'
        : 'has an empty slot'
    return `${carrierNoun === 'couple' ? 'Couple' : 'Group'} ${state.carrier.groups.indexOf(blocked.group) + 1} ${what}`
  }
  const blockText = firstBlock()

  const itemCount = kind === 'multisample' ? msGroups.length : kind === 'cohort' ? 1 : carrierGroups.length
  const hasWork =
    kind === 'multisample'
      ? msGroups.length > 0
      : kind === 'cohort'
      ? cohortMembers.length > 0
      : carrierGroups.length > 0
  // What the run costs (model/pricing groupPrice): carrier_price per carrier
  // analysis; multisample and CNV cohort are free. `org` is the panel's prices
  // (usePrices) or an /organisation/details response.
  const price = kind ? groupPrice(kind, state, org) : null
  const balance = balanceOf(org)

  // ------------------------------------------------------------ state setters
  const setMultisampleGroups = useCallback((groups) => setState((prev) => ({ ...prev, multisample: { groups } })), [])
  const toggleCohort = useCallback(
    (row) => setState((prev) => ({ ...prev, cohort: toggleCohortMember(setCohortKit(prev.cohort, kitId), row) })),
    [kitId],
  )
  const clearCohort = useCallback(
    () => setState((prev) => ({ ...prev, cohort: { kitId: prev.cohort.kitId, members: [] } })),
    [],
  )
  const setCarrierGroups = useCallback((groups) => setState((prev) => ({ ...prev, carrier: { groups } })), [])

  // ------------------------------------------------------------ run
  const refreshAfterStart = useCallback(
    (runKind) => {
      queryClient.invalidateQueries('all-analysis')
      queryClient.invalidateQueries(BATCHES_QUERY_KEY)
      queryClient.invalidateQueries(ANALYSIS_STATS_KEY)
      queryClient.invalidateQueries('organisation-details')
      queryClient.invalidateQueries(['new-analysis', 'finished'])
      if (runKind === 'cohort') queryClient.invalidateQueries(['new-analysis', 'cnv-cohorts'])
    },
    [queryClient],
  )

  const buildItems = () => {
    if (kind === 'multisample') {
      const date = new Date()
      return {
        items: msGroups.map((group) => ({
          key: group.uid,
          label: groupLabel(group, state.multisample.groups.indexOf(group)),
          request: buildMultisampleRequest(group, { date }),
        })),
      }
    }
    if (kind === 'cohort') {
      return {
        name: cohortName,
        items: [
          {
            key: 'cohort',
            label: cohortName,
            request: buildCohortRequest({
              method: cohortMethod,
              name: cohortName,
              pairIds: cohortMembers.map((m) => m.pairId),
              bedFileId,
            }),
          },
        ],
      }
    }
    const batch = newBatch((batchName || '').trim() || suggestBatchName(wf))
    const config = { id: carrierConfig.id, samples_required: carrierConfig.samplesRequired }
    return {
      batch,
      items: carrierGroups.map((group) => ({
        key: group.uid,
        label: `${carrierNoun === 'couple' ? 'Couple' : 'Group'} ${state.carrier.groups.indexOf(group) + 1}`,
        request: buildCarrierRequest({ config, members: group.members, batch }),
      })),
    }
  }

  const execute = async (items, { runKind, batch = null, name = '', retry = false }) => {
    const controller = new AbortController()
    abortRef.current = controller
    runStartedAtRef.current = Date.now()
    setRun((prev) => {
      const base =
        retry && prev
          ? prev
          : { kind: runKind, order: items.map((i) => i.key), keys: {}, status: {}, items: {}, batch, name }
      const keys = { ...base.keys }
      const status = { ...base.status }
      const byKey = { ...base.items }
      items.forEach((item) => {
        keys[item.key] = item.label
        status[item.key] = { status: 'queued' }
        byKey[item.key] = item
      })
      return { ...base, keys, status, items: byKey, running: true }
    })
    setView('start')

    const results = await startRequests(
      items.map((item) => ({ key: item.key, request: item.request, error: item.error })),
      {
        signal: controller.signal,
        onProgress: (index, status, detail) => {
          const key = items[index]?.key
          if (!key || !mountedRef.current) return
          setRun((prev) => (prev ? { ...prev, status: { ...prev.status, [key]: { status, detail } } } : prev))
        },
      },
    )
    abortRef.current = null

    const started = results.filter((r) => r.status === 'started')
    const unknown = results.filter((r) => r.status === 'unknown')
    if (started.length || unknown.length) refreshAfterStart(runKind)

    // What started, or may have started, leaves the state (and so the draft):
    // retrying it could start and charge it twice.
    const gone = new Set([...started, ...unknown].map((r) => r.key))
    if (mountedRef.current) {
      setRun((prev) => (prev ? { ...prev, running: false } : prev))
      if (gone.size) setState((prev) => pruneStarted(prev, runKind, gone))
    } else if (gone.size && onStateChangeRef.current) {
      // Closed while the run went on ("Close anyway"): the panel keeps its draft
      // without what started, so it does not come back on the next open.
      onStateChangeRef.current(pruneStarted(stateRef.current, runKind, gone))
    }

    const labels = Object.fromEntries(items.map((i) => [i.key, i.label]))
    if (started.length && onStarted) {
      const payload = results.map((r) => ({ ...r, sampleKey: labels[r.key] ?? r.key }))
      payload.kind = runKind
      payload.batch = runKind === 'carrier' ? batch : null
      payload.workflow = wf
      payload.workflowSummary = describeWorkflow(wf)
      onStarted(payload)
    }
    return results
  }

  const enterReview = () => {
    if (blockText) {
      setAttempted(true)
      if (onShowErrors) onShowErrors()
      return
    }
    reviewEnteredAtRef.current = Date.now()
    setView('review')
  }

  const startReviewed = () => {
    // The second click of a double-click on Review lands here.
    if (Date.now() - reviewEnteredAtRef.current < START_GUARD_MS) return
    if (blockText || abortRef.current) return
    const { items, batch, name } = buildItems()
    if (!items.length) return
    execute(items, { runKind: kind, batch, name })
  }

  const retryFailed = () => {
    if (!run || run.running) return
    const failed = run.order
      .filter((key) => ['failed', 'skipped'].includes(run.status[key]?.status))
      .map((key) => run.items[key])
      .filter(Boolean)
    if (failed.length) execute(failed, { runKind: run.kind, batch: run.batch, name: run.name, retry: true })
  }

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort()
  }, [])

  // ------------------------------------------------------------ inert
  const running = Boolean(run?.running)
  if (!kind) {
    return {
      view: 'compose',
      samplesArea: null,
      body: null,
      footer: INERT_FOOTER,
      headerTitle: '',
      headerCount: '',
      running,
      stop,
      showErrors: false,
    }
  }

  const errorsShown = Boolean(showErrors) || attempted

  // ------------------------------------------------------------ header
  let headerTitle = ''
  let headerCount = ''
  if (kind === 'multisample') {
    headerTitle = 'New multisample analysis'
    if (msGroups.length) {
      headerCount = `${plural(msGroups.length, 'group', 'groups')}, ${plural(msSampleCount, 'sample', 'samples')}`
    }
  } else if (kind === 'cohort') {
    headerTitle = 'New CNV cohort'
    if (cohortMembers.length) headerCount = `${cohortMembers.length} selected`
  } else if (carrierGroups.length) {
    headerCount = plural(carrierGroups.length, carrierNoun, `${carrierNoun}s`)
  }

  // ------------------------------------------------------------ samples area
  let samplesArea = null
  if (kind === 'multisample') {
    samplesArea = React.createElement(MultisampleArea, {
      groups: state.multisample.groups,
      onChange: setMultisampleGroups,
      showErrors: errorsShown,
    })
  } else if (kind === 'cohort') {
    samplesArea = React.createElement(CohortArea, {
      cohort: state.cohort,
      kitId,
      kitName: wf.kitName,
      onToggle: toggleCohort,
      onClear: clearCohort,
    })
  } else {
    samplesArea = React.createElement(CarrierArea, {
      groups: state.carrier.groups,
      config: carrierConfig,
      candidates,
      allowFastq,
      showErrors: errorsShown,
      onChange: setCarrierGroups,
    })
  }

  // ------------------------------------------------------------ body
  let body = null
  if (view === 'review') {
    body = React.createElement(GroupReviewView, {
      kind,
      groups: kind === 'multisample' ? msGroups : carrierGroups,
      methodLabel: METHOD_LABEL[cohortMethod] || 'Genesus CNV',
      name: cohortName,
      kitName: wf.kitName || kitId,
      members: cohortMembers,
      configTitle: carrierConfig?.title || '',
      samplesRequired: carrierConfig?.samplesRequired,
      price,
      balance,
    })
  } else if (view === 'start' && run) {
    body = React.createElement(GroupStartView, { run })
  }

  // ------------------------------------------------------------ footer
  let primary = null
  let secondary = null
  let text = ''
  let emphasis = false

  if (view === 'compose') {
    let label
    if (kind === 'multisample')
      label = msGroups.length ? `Review ${plural(msGroups.length, 'group', 'groups')}` : 'Review'
    else if (kind === 'cohort') label = 'Review cohort'
    else
      label = carrierGroups.length ? `Review ${plural(carrierGroups.length, carrierNoun, `${carrierNoun}s`)}` : 'Review'
    primary = { label, disabled: !hasWork, shortcut: true, onClick: enterReview }

    if (attempted && blockText && hasWork) {
      text = blockText
      emphasis = true
    } else if (!hasWork) {
      if (kind === 'multisample') text = 'Pick finished analyses to continue'
      else if (kind === 'cohort')
        text = kitId ? 'Select finished analyses to continue' : 'Choose a capture kit to continue'
      else text = carrierConfig ? `Add a ${carrierNoun} to continue` : 'Choose a carrier panel to continue'
    } else if (kind === 'multisample') {
      text = `${plural(msGroups.length, 'group', 'groups')} · ${plural(msSampleCount, 'sample', 'samples')} · No charge`
    } else if (kind === 'cohort') {
      text = [METHOD_LABEL[cohortMethod], wf.kitName, `${cohortMembers.length} selected`, 'No charge']
        .filter(Boolean)
        .join(' · ')
    } else {
      const priceText = price.known ? `estimated ${creditsText(price.total)}` : 'price confirmed at start'
      const balanceText =
        balance === null
          ? ''
          : price.known && price.short > 0
          ? `balance ${formatCredits(balance)} is not enough`
          : `balance ${formatCredits(balance)}`
      text = [plural(carrierGroups.length, carrierNoun, `${carrierNoun}s`), priceText, balanceText]
        .filter(Boolean)
        .join(' · ')
    }
  } else if (view === 'review') {
    secondary = { label: 'Back', onClick: () => setView('compose') }
    const startLabel = kind === 'cohort' ? 'Build cohort' : `Start ${plural(itemCount, 'analysis', 'analyses')}`
    primary = { label: startLabel, disabled: Boolean(blockText) || !itemCount, onClick: startReviewed }
    // Multisample says "No charge." in the review's Price section only.
    if (kind === 'carrier' && balance !== null) text = `Balance ${formatCredits(balance)}`
  } else if (view === 'start' && run) {
    const failedCount = run.order.filter((key) => ['failed', 'skipped'].includes(run.status[key]?.status)).length
    const startedCount = run.order.filter((key) => run.status[key]?.status === 'started').length
    const close = { label: 'Close', onClick: onClose }
    const goToBatch =
      run.kind === 'carrier' && run.batch?.id
        ? {
            label: 'Go to batch',
            onClick: () => {
              navigate(batchPath(run.batch.id))
              if (onClose) onClose()
            },
          }
        : null
    if (run.running) {
      if (run.order.length > 1) {
        secondary = {
          label: 'Stop',
          onClick: () => {
            // Stop takes the slot of Start and Retry: the second click of a
            // double-click on them lands here and is not a request to stop.
            if (Date.now() - runStartedAtRef.current < START_GUARD_MS) return
            stop()
          },
        }
        text = 'Starts not sent yet stop after the current one.'
      }
    } else if (failedCount) {
      primary = { label: `Retry ${failedCount}`, onClick: retryFailed }
      secondary = startedCount && goToBatch ? goToBatch : close
    } else if (startedCount) {
      if (goToBatch) {
        primary = goToBatch
        secondary = close
      } else {
        primary = close
      }
    } else {
      secondary = close
    }
  }

  return {
    view,
    samplesArea,
    body,
    footer: { primary, secondary, text, emphasis },
    headerTitle,
    headerCount,
    running,
    stop,
    showErrors: attempted,
  }
}

export default useGroupFlow
